import React from 'react';
import { Ticket } from '../../../types';
import Button from '../../ui/Button/Button';
import styles from './TicketCard.module.css';

interface TicketCardProps {
  ticket: Ticket;
  quantity: number;
  onIncrement: () => void;
  onDecrement: () => void;
  disabled?: boolean;
  getTranslation: (key: string) => string;
}

const TicketCard: React.FC<TicketCardProps> = ({
  ticket,
  quantity,
  onIncrement,
  onDecrement,
  disabled = false,
  getTranslation
}) => {
  const isInventoryTracked = ticket.trackInventory === "1" || ticket.trackInventory === true;
  const availableCount = isInventoryTracked 
    ? ticket.inventory - ticket.soldInventory 
    : Infinity;
  const isSoldOut = isInventoryTracked && availableCount <= 0;
  const isRunningOut = isInventoryTracked && availableCount > 0 && availableCount <= 5;

  const formatPrice = (price: number | string) => {
    const numericPrice = typeof price === 'string' ? parseFloat(price) : Number(price);
    if (isNaN(numericPrice) || numericPrice === 0 || Math.abs(numericPrice) < 0.01) {
      return getTranslation('events.free');
    }
    return `${numericPrice.toLocaleString('en-US')} TL`;
  };

  return (
    <div className={`${styles.ticketCard} ${isSoldOut ? styles.soldOut : ''}`}>
      <div className={styles.ticketHeader}>
        <div className={styles.ticketTitleSection}>
          <h3 className={styles.ticketTitle}>{ticket.title}</h3>
          <p className={styles.ticketPrice}>{formatPrice(ticket.price)}</p>
        </div>
        {isInventoryTracked && (
          <div className={styles.inventoryBadge}>
            {isSoldOut ? (
              <span className={styles.soldOutBadge}>{getTranslation('events.soldOut')}</span>
            ) : isRunningOut ? (
              <span className={styles.runningOutBadge}>
                {getTranslation('events.onlyLeft')} {availableCount}
              </span>
            ) : (
              <span className={styles.availableBadge}>
                {availableCount} {getTranslation('events.available')}
              </span>
            )}
          </div>
        )}
      </div>

      {ticket.description && (
        <p className={styles.ticketDescription}>{ticket.description}</p>
      )}

      <div className={styles.ticketActions}>
        <div className={styles.quantityControls}>
          <Button
            variant="secondary"
            size="small"
            onClick={onDecrement}
            disabled={quantity === 0 || disabled}
            className={styles.quantityButton}
          >
            −
          </Button>
          <span className={styles.quantity}>{quantity}</span>
          <Button
            variant="secondary"
            size="small"
            onClick={onIncrement}
            disabled={isSoldOut || (isInventoryTracked && quantity >= availableCount) || disabled}
            className={styles.quantityButton}
          >
            +
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TicketCard;

